import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Chip,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  motion,
  AnimatePresence,
  useScroll,
  useSpring,
  useInView,
} from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import CloseIcon from '@mui/icons-material/Close';

// ─── Shared visual tokens (match rest of the site) ──────────────────────────
const BG = '#000000';
const BORDER = '1px solid rgba(255,255,255,0.08)';
const BORDER_SOFT = '1px solid rgba(255,255,255,0.05)';
const TEXT = '#ffffff';
const TEXT_DIM = 'rgba(255,255,255,0.55)';
const TEXT_MUTED = 'rgba(255,255,255,0.35)';

// ─── Block renderers ────────────────────────────────────────────────────────
function Paragraph({ text }) {
  return (
    <Typography
      sx={{
        fontFamily: 'Inter',
        fontWeight: 300,
        fontSize: { xs: '0.95rem', md: '1rem' },
        lineHeight: 1.75,
        color: 'rgba(255,255,255,0.72)',
        mb: 2,
      }}
    >
      {text}
    </Typography>
  );
}

function Bullets({ items }) {
  return (
    <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0, mb: 2 }}>
      {items.map((it, i) => {
        const isRich = typeof it === 'object' && it !== null;
        const label = isRich ? it.label : null;
        const text = isRich ? it.text : it;
        return (
          <Box
            key={i}
            component="li"
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              mb: 1.25,
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.55)',
                mt: '10px',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 300,
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.72)',
              }}
            >
              {label && (
                <Box
                  component="span"
                  sx={{ color: '#fff', fontWeight: 600 }}
                >
                  {label}:{' '}
                </Box>
              )}
              {text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function SubHeading({ text }) {
  return (
    <Typography
      sx={{
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: '0.95rem',
        color: '#fff',
        letterSpacing: '-0.01em',
        mt: 2.5,
        mb: 1.25,
      }}
    >
      {text}
    </Typography>
  );
}

function Warning({ bold, text }) {
  return (
    <Box
      sx={{
        border: '1px solid rgba(250, 204, 21, 0.25)',
        background: 'rgba(250, 204, 21, 0.04)',
        borderRadius: 1.5,
        p: 2.5,
        mb: 2,
      }}
    >
      {bold && (
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'rgba(250, 204, 21, 0.9)',
            mb: 1,
          }}
        >
          {bold}
        </Typography>
      )}
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '0.9rem',
          lineHeight: 1.65,
          color: '#fff',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function BlockRenderer({ block }) {
  if (block.type === 'p') return <Paragraph text={block.text} />;
  if (block.type === 'ul') return <Bullets items={block.items} />;
  if (block.type === 'h3') return <SubHeading text={block.text} />;
  if (block.type === 'warning')
    return <Warning bold={block.bold} text={block.text} />;
  return null;
}

// ─── One section card (expandable, reveal-on-scroll) ───────────────────────
function SectionCard({ section, isCollapsed, onToggle, registerRef }) {
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { amount: 0.15, once: true });

  return (
    <motion.section
      id={section.id}
      ref={(el) => {
        cardRef.current = el;
        registerRef(section.id, el);
      }}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      style={{ scrollMarginTop: 96 }}
    >
      <Box
        sx={{
          mb: 3,
          border: BORDER,
          borderRadius: 2,
          background: 'rgba(255,255,255,0.015)',
          overflow: 'hidden',
        }}
      >
        {/* Header — click to collapse/expand */}
        <Box
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: { xs: 2.5, md: 3.5 },
            py: 2.5,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 0.2s ease',
            '&:hover': { background: 'rgba(255,255,255,0.02)' },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.1em',
              color: TEXT_MUTED,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 30,
            }}
          >
            {String(section.number).padStart(2, '0')}
          </Box>
          <Typography
            component="h2"
            sx={{
              flex: 1,
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '1.05rem', md: '1.25rem' },
              color: TEXT,
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
            }}
          >
            {section.title}
          </Typography>
          <motion.div
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'flex' }}
          >
            <KeyboardArrowUpIcon
              sx={{ color: TEXT_MUTED, fontSize: 22 }}
            />
          </motion.div>
        </Box>

        {/* Body */}
        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <Box
                sx={{
                  px: { xs: 2.5, md: 3.5 },
                  pb: 3.5,
                  pt: 0.5,
                  borderTop: BORDER_SOFT,
                }}
              >
                {section.blocks.map((b, i) => (
                  <BlockRenderer key={i} block={b} />
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </motion.section>
  );
}

// ─── Contact card (with copy-to-clipboard) ─────────────────────────────────
function ContactCard({ contact }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(contact.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };
  return (
    <Box
      sx={{
        mt: 6,
        p: { xs: 3, md: 4 },
        borderRadius: 2,
        border: BORDER,
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '0.68rem',
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          mb: 1.5,
        }}
      >
        Get in touch
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 800,
          fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
          color: TEXT,
          letterSpacing: '-0.03em',
          mb: 2,
        }}
      >
        {contact.name}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '1rem',
            color: '#fff',
          }}
        >
          {contact.email}
        </Typography>
        <Tooltip
          title={copied ? 'Copied' : 'Copy email'}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              color: copied ? '#4ade80' : TEXT_MUTED,
              border: BORDER,
              borderRadius: 1,
              width: 30,
              height: 30,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: '#fff',
                background: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <AnimatePresence mode="wait">
              {copied ? (
                <motion.div
                  key="check"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.6, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: 'flex' }}
                >
                  <CheckIcon sx={{ fontSize: 16 }} />
                </motion.div>
              ) : (
                <motion.div
                  key="copy"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.6, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: 'flex' }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </motion.div>
              )}
            </AnimatePresence>
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 300,
          fontSize: '0.9rem',
          lineHeight: 1.7,
          color: TEXT_DIM,
        }}
      >
        {contact.address}
      </Typography>
    </Box>
  );
}

// ─── Table of contents (desktop sticky rail + mobile bottom sheet) ────────
function TableOfContents({ sections, activeId, onJump, filterQuery }) {
  return (
    <Box component="nav" aria-label="Sections">
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '0.68rem',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: TEXT_MUTED,
          mb: 2,
        }}
      >
        Contents
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {sections.map((s) => {
          const isActive = activeId === s.id;
          const isMatch =
            !filterQuery ||
            sectionMatchesQuery(s, filterQuery.toLowerCase());
          return (
            <Box
              key={s.id}
              onClick={() => onJump(s.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: isMatch ? 1 : 0.25,
                background: isActive ? 'rgba(255,255,255,0.05)' : 'transparent',
                '&:hover': { background: 'rgba(255,255,255,0.05)' },
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 14,
                  borderRadius: 1,
                  background: isActive ? '#fff' : 'transparent',
                  transition: 'background 0.2s ease',
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.82rem',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                  lineHeight: 1.3,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: TEXT_MUTED,
                    fontVariantNumeric: 'tabular-nums',
                    mr: 1,
                  }}
                >
                  {String(s.number).padStart(2, '0')}
                </Box>
                {s.title}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function sectionMatchesQuery(section, q) {
  if (!q) return true;
  if (section.title.toLowerCase().includes(q)) return true;
  for (const b of section.blocks) {
    if (b.type === 'p' && b.text.toLowerCase().includes(q)) return true;
    if (b.type === 'h3' && b.text.toLowerCase().includes(q)) return true;
    if (b.type === 'warning' && b.text.toLowerCase().includes(q)) return true;
    if (b.type === 'ul') {
      for (const it of b.items) {
        const t = typeof it === 'object' ? `${it.label} ${it.text}` : it;
        if (t.toLowerCase().includes(q)) return true;
      }
    }
  }
  return false;
}

// ─── Main component ────────────────────────────────────────────────────────
export default function LegalDocument({
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  icon,
  sections,
  contact,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [collapsed, setCollapsed] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const sectionRefs = useRef(new Map());

  // Scroll-progress bar
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 22,
    mass: 0.4,
  });

  const registerRef = (id, el) => {
    if (el) sectionRefs.current.set(id, el);
  };

  // Track active section via IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the top-most section in view
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    sectionRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  // Keyboard nav: J = next, K = previous
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key !== 'j' && e.key !== 'k') return;
      const ids = sections.map((s) => s.id);
      const idx = ids.indexOf(activeId);
      const next =
        e.key === 'j'
          ? Math.min(ids.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      jumpTo(ids[next]);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeId, sections]);

  const jumpTo = (id) => {
    const el = sectionRefs.current.get(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileTocOpen(false);
    // Ensure section is expanded when jumped to
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleSection = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collapseAll = () =>
    setCollapsed(new Set(sections.map((s) => s.id)));
  const expandAll = () => setCollapsed(new Set());

  const visibleSections = useMemo(() => {
    if (!query) return sections;
    return sections.filter((s) =>
      sectionMatchesQuery(s, query.toLowerCase())
    );
  }, [query, sections]);

  return (
    <Box sx={{ background: BG, color: TEXT, minHeight: '100vh' }}>
      {/* Reading progress bar */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background:
            'linear-gradient(to right, rgba(255,255,255,0.9), rgba(255,255,255,0.35))',
          transformOrigin: '0 50%',
          scaleX: smoothProgress,
          zIndex: 1200,
        }}
      />

      {/* Hero */}
      <Box
        sx={{
          pt: { xs: 10, md: 14 },
          pb: { xs: 6, md: 10 },
          px: { xs: 3, md: 8 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Ambient glow behind hero */}
        <Box
          sx={{
            position: 'absolute',
            top: -200,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            maxWidth: 780,
            mx: 'auto',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            style={{
              display: 'inline-block',
              marginBottom: 24,
            }}
          >
            {icon}
          </motion.div>

          {eyebrow && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '0.68rem',
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: TEXT_MUTED,
                  mb: 2.5,
                }}
              >
                {eyebrow}
              </Typography>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 900,
                fontSize: 'clamp(2.4rem, 6vw, 4.4rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.045em',
                color: '#fff',
                mb: 2,
              }}
            >
              {title}
            </Typography>
          </motion.div>

          {subtitle && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.32 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 300,
                  fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
                  lineHeight: 1.6,
                  color: TEXT_DIM,
                  maxWidth: 560,
                  mx: 'auto',
                  mb: 3,
                }}
              >
                {subtitle}
              </Typography>
            </motion.div>
          )}

          {lastUpdated && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              style={{ display: 'inline-block' }}
            >
              <Chip
                label={`Last updated · ${lastUpdated}`}
                size="small"
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  color: TEXT_DIM,
                  background: 'rgba(255,255,255,0.04)',
                  border: BORDER,
                  height: 24,
                }}
              />
            </motion.div>
          )}
        </Box>
      </Box>

      {/* Body: TOC + Content grid */}
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 3, md: 6 },
          pb: { xs: 10, md: 14 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
          gap: { xs: 0, md: 6 },
          alignItems: 'start',
        }}
      >
        {/* Sticky TOC (desktop) */}
        {isDesktop && (
          <Box
            sx={{
              position: 'sticky',
              top: 96,
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
              pr: 1,
              // Custom scrollbar
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 2,
              },
            }}
          >
            <TableOfContents
              sections={sections}
              activeId={activeId}
              onJump={jumpTo}
              filterQuery={query}
            />
          </Box>
        )}

        {/* Content column */}
        <Box sx={{ minWidth: 0 }}>
          {/* Toolbar: search + expand/collapse all */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 3,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 220,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 1,
                border: BORDER,
                borderRadius: 1.5,
                background: 'rgba(255,255,255,0.02)',
                transition: 'border 0.2s ease',
                '&:focus-within': {
                  borderColor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <SearchIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this document…"
                fullWidth
                sx={{
                  color: '#fff',
                  fontFamily: 'Inter',
                  fontSize: '0.9rem',
                  '& input::placeholder': {
                    color: TEXT_MUTED,
                    opacity: 1,
                  },
                }}
              />
              {query && (
                <IconButton
                  size="small"
                  onClick={() => setQuery('')}
                  sx={{ color: TEXT_MUTED, p: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                label="Expand all"
                clickable
                onClick={expandAll}
                sx={chipToggleSx}
              />
              <Chip
                label="Collapse all"
                clickable
                onClick={collapseAll}
                sx={chipToggleSx}
              />
            </Box>
          </Box>

          {/* Sections */}
          {visibleSections.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                border: BORDER,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.015)',
              }}
            >
              <Typography sx={{ color: TEXT_DIM, fontFamily: 'Inter' }}>
                No sections match “{query}”.
              </Typography>
            </Box>
          ) : (
            visibleSections.map((s) => (
              <SectionCard
                key={s.id}
                section={s}
                isCollapsed={collapsed.has(s.id)}
                onToggle={() => toggleSection(s.id)}
                registerRef={registerRef}
              />
            ))
          )}

          {/* Contact card */}
          {contact && <ContactCard contact={contact} />}

          {/* Keyboard hint */}
          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: BORDER_SOFT,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '0.75rem',
                color: TEXT_MUTED,
                display: { xs: 'none', md: 'block' },
              }}
            >
              Tip: press{' '}
              <Box component="kbd" sx={kbdSx}>
                J
              </Box>{' '}
              /{' '}
              <Box component="kbd" sx={kbdSx}>
                K
              </Box>{' '}
              to jump between sections.
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                color: TEXT_MUTED,
              }}
            >
              Built with care by AI Guru.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mobile: floating TOC button + bottom sheet */}
      {!isDesktop && (
        <>
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1100,
            }}
          >
            <IconButton
              onClick={() => setMobileTocOpen(true)}
              sx={{
                width: 52,
                height: 52,
                background: '#fff',
                color: '#000',
                boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
                '&:hover': { background: '#f0f0f0' },
              }}
              aria-label="Open table of contents"
            >
              <MenuBookIcon />
            </IconButton>
          </motion.div>

          <AnimatePresence>
            {mobileTocOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileTocOpen(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 1200,
                  }}
                />
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{
                    duration: 0.35,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    maxHeight: '75vh',
                    background: '#0a0a0a',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '20px 20px 0 0',
                    padding: '24px 24px 32px',
                    zIndex: 1201,
                    overflowY: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#fff',
                      }}
                    >
                      Jump to section
                    </Typography>
                    <IconButton
                      onClick={() => setMobileTocOpen(false)}
                      sx={{ color: TEXT_MUTED }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <TableOfContents
                    sections={sections}
                    activeId={activeId}
                    onJump={jumpTo}
                    filterQuery={query}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </Box>
  );
}

const chipToggleSx = {
  fontFamily: 'Inter',
  fontSize: '0.72rem',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.6)',
  background: 'rgba(255,255,255,0.02)',
  border: BORDER,
  height: 30,
  '&:hover': {
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
  },
};

const kbdSx = {
  display: 'inline-block',
  px: 0.75,
  py: 0.25,
  mx: 0.25,
  border: BORDER,
  borderRadius: 0.5,
  background: 'rgba(255,255,255,0.03)',
  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  fontSize: '0.7rem',
  color: '#fff',
};
